<template>
    <div class="grid w-full grid-cols-[min-content_1fr] gap-x-3 gap-y-4">
        <div
            v-cy="'toastMessageIcon'"
            class="flex size-8 items-center justify-center rounded"
            :class="severityClasses.iconWrapper"
        >
            <div
                class="flex size-1/2 items-center justify-center rounded-full"
                :class="{'bg-white': severity === 'success' || severity === 'error'}"
            >
                <check-icon
                    v-if="severity === 'success'"
                    v-cy="'toastMessageSuccessIcon'"
                    class="text-green size-1/2"
                />
                <triangle-exclamation-icon
                    v-else-if="severity === 'warn'"
                    class="size-full text-white"
                />
                <info-icon v-else-if="severity === 'info'" class="size-full text-white" />
                <close-icon-bold v-else-if="severity === 'error'" class="text-red size-1/2" />
            </div>
        </div>
        <h4 class="mr-1 flex items-center text-base/[18px] font-bold" v-text="summary" />
        <div v-if="showDetails" class="col-start-2 flex flex-col items-stretch gap-2">
            <p v-if="detail" class="text-base/5" :class="severityClasses.detail" v-html="detail" />
            <div class="mt-1 flex text-sm">
                <div
                    v-if="link"
                    v-cy="'toastMessageLink'"
                    class="me-2 underline"
                    @click="linkCallback"
                >
                    <router-link
                        v-if="link.route"
                        active-class=""
                        exact-active-class=""
                        :to="link.route"
                    >
                        {{ link.label }}
                    </router-link>
                    <a
                        v-else-if="link.url"
                        v-cy="'toastMessageURL'"
                        class="flex items-center gap-1"
                        :class="severityClasses.link"
                        :href="link.url"
                        target="_blank"
                    >
                        <external-link-icon
                            v-if="isExternalLink"
                            v-cy="'toastMessageExternalLinkIcon'"
                            class="!h-4 !w-4"
                        />
                        {{ link.label }}
                    </a>
                    <a
                        v-else-if="link.callbackFn"
                        class="flex items-center gap-1"
                        :class="severityClasses.link"
                        href="#"
                        target="_blank"
                        @click.prevent="link.callbackFn"
                    >
                        <external-link-icon v-if="isExternalLink" class="!h-4 !w-4" />
                        {{ link.label }}
                    </a>
                </div>
                <span
                    v-if="date"
                    v-cy="'toastMessageDate'"
                    class="ml-auto text-gray dark:text-gray-light"
                    v-text="relativeTimeFormatted"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed} from 'vue';
import CheckIcon from '../icons/CheckIcon.vue';
import InfoIcon from '../icons/InfoIcon.vue';
import CloseIconBold from '../icons/CloseIconBold.vue';
import configDay from '../../mixins/configDay';
import TriangleExclamationIcon from '../icons/TriangleExclamationIcon.vue';
import ExternalLinkIcon from '../icons/ExternalLinkIcon.vue';
import {useMatomoEventTracker} from '../../matomoTracking';

const props = defineProps({
    severity: {
        type: String,
        required: true,
    },
    summary: {
        type: String,
        required: true,
    },
    detail: {
        type: String,
        default: null,
    },
    date: {
        type: [Date, null],
        default: null,
    },
    link: {
        type: [Object, null],
        default: () => null,
    },
    closeCallback: {
        type: Function,
        default: () => () => {},
    },
    preventCloseOnClick: Boolean,
    notificationId: {
        type: String,
        default: null,
    },
    onLinkClick: {
        type: Function,
        default: null,
    },
});

const relativeTimeFormatted = computed(() => {
    if (!props.date) {
        return '';
    }
    return configDay.methods.getFormattedDate(props.date);
});

const severityClasses = computed(() => {
    switch (props.severity) {
        case 'error':
            return {
                iconWrapper: 'bg-red-500 text-red-500',
                detail: 'text-red-500 dark:text-red-200',
                link: 'hover:text-red-500 dark:hover:text-red-400',
            };
        case 'success':
            return {
                iconWrapper: 'bg-green-500 text-green-500',
                detail: 'text-green-500 dark:text-green-200',
                link: 'hover:text-green-500 dark:hover:text-green-400',
            };
        case 'info':
            return {
                iconWrapper: 'bg-blue-500 text-blue-500',
                detail: 'text-blue-500 dark:text-blue-200',
                link: 'hover:text-blue-500 dark:hover:text-blue-400',
            };
        case 'warn':
            return {
                iconWrapper: 'bg-yellow-500 text-yellow-500',
                detail: 'text-yellow-600 dark:text-yellow-200',
                link: 'hover:text-yellow-500 dark:hover:text-yellow-400',
            };
        default:
            return '';
    }
});

const showDetails = computed(() => props.detail || props.date || props.link);

const isExternalLink = computed(() => {
    if (!props.link) {
        return false;
    }
    if (props.link?.route || props.link?.callbackFn) {
        return false;
    }
    const url = props.link.url;
    return url.trim().startsWith('http') && url.indexOf(window.location.origin) === -1;
});

function linkCallback() {
    if (props.link?.url) {
        useMatomoEventTracker('Toast').trackEvent('Toast link clicked', props.link.url);
    }

    // Call the onLinkClick callback if provided
    if (props.onLinkClick && props.notificationId) {
        props.onLinkClick(props.notificationId);
    }

    if (props.preventCloseOnClick) {
        return;
    }

    props.closeCallback();
}
</script>
